import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Phone, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiMessage } from '../lib/helpers';
import { GuardianFields } from './GuardianFields';
import {
  attachGuardian,
  detachGuardian,
  emptyGuardian,
  guardianIsComplete,
  type ChildRow,
  type GuardianDraft,
} from './kids-area';

interface Props {
  child: ChildRow;
  onChanged: (child: ChildRow) => void;
  canManage: boolean;
}

/**
 * Who the venue may hand this child back to, editable where the desk stands.
 *
 * The list is the whole of the handover rule stated in advance. A family whose
 * second parent is not on the record will need a manager's signature when he
 * turns up at six o'clock, and the moment to fix that is now — while the child
 * is being checked in and the parent doing the checking-in is standing there —
 * not at the counter with a queue behind them.
 *
 * Removing the last guardian is refused by the server, not by a disabled button:
 * a child with nobody to call is the state the record exists to prevent, and the
 * refusal says so in words the desk can act on.
 */
export function ChildGuardians({ child, onChanged, canManage }: Props) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<GuardianDraft[]>([{ ...emptyGuardian }]);
  const [busy, setBusy] = useState<string | null>(null);

  const add = async () => {
    if (!guardianIsComplete(draft[0])) return;
    setBusy('add');
    try {
      onChanged(await attachGuardian(child.id, draft[0]));
      setDraft([{ ...emptyGuardian }]);
      setAdding(false);
      toast.success(t('spaces.kids.guardianAdded'));
    } catch (error) {
      toast.error(apiMessage(error, t('spaces.resources.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (guardianId: string) => {
    setBusy(guardianId);
    try {
      onChanged(await detachGuardian(child.id, guardianId));
      toast.success(t('spaces.kids.guardianRemoved'));
    } catch (error) {
      toast.error(apiMessage(error, t('spaces.resources.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <p className="text-sm font-medium">{t('spaces.kids.guardians')}</p>

      <ul className="divide-y text-sm">
        {child.guardians.map((link) => (
          <li key={link.guardianId} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="truncate font-medium">
                {link.guardian.name}
                {link.relationship ? (
                  <span className="ms-2 text-xs text-ink-muted">{link.relationship}</span>
                ) : null}
                {link.isPrimary && (
                  <span className="ms-2 inline-flex items-center gap-1 text-xs text-accent">
                    <Star className="h-3 w-3" aria-hidden />
                    {t('spaces.kids.callFirst')}
                  </span>
                )}
              </p>
              <p className="flex items-center gap-1 text-xs text-ink-muted" dir="ltr">
                <Phone className="h-3 w-3 shrink-0" aria-hidden />
                {link.guardian.phone}
                {link.guardian.backupPhone ? ` · ${link.guardian.backupPhone}` : ''}
              </p>
            </div>
            {canManage && (
              <button
                type="button"
                disabled={busy === link.guardianId}
                onClick={() => void remove(link.guardianId)}
                className="flex shrink-0 items-center gap-1 text-xs text-ink-muted hover:underline disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                {t('common.delete')}
              </button>
            )}
          </li>
        ))}
        {child.guardians.length === 0 && (
          <li className="py-2 text-xs text-amber-700 dark:text-amber-400">
            {t('spaces.kids.noGuardian')}
          </li>
        )}
      </ul>

      {canManage &&
        (adding ? (
          <div className="space-y-2">
            <GuardianFields guardians={draft} onChange={setDraft} minimum={1} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={busy === 'add' || !guardianIsComplete(draft[0])}
                onClick={() => void add()}
              >
                {t('spaces.kids.saveGuardian')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            {t('spaces.kids.addGuardian')}
          </Button>
        ))}
    </div>
  );
}
